import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { API, environment } from '@core/envs';
import { ICoordate, IOtherPhone, IUserFromBFF, IUserProfileResponse } from '@core/interface';
import { GoogleMapsApiService } from '@core/services';
import { firstValueFrom, tap } from 'rxjs';
import { ValidateUserService } from '../validate-users/validate-user.service';
import { GetCountryService } from '../country/country.service';
import { getLengthPhone } from '@core/utils';
import { StorageKeys } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  readonly #http = inject(HttpClient);
  readonly #googleMapsApiService = inject(GoogleMapsApiService);
  readonly #validateUserService = inject(ValidateUserService);
  readonly #getCountryService = inject(GetCountryService);

  readonly #userProfileSignal = signal<IUserProfileResponse | null>(null);

  userCoordinates: ICoordate | undefined;
  userComputed = computed<IUserProfileResponse | null>(() => this.#userProfileSignal());

  /**
   * @deprecated
   * Define o usuário com base nos dados fornecidos.
   * Este método atualiza as informações do usuário e, se o CEP estiver presente,
   * busca as coordenadas do endereço usando o serviço Heremaps.
   * Grava os dados do usuário no sessionStorage e atualiza o sinal `userProfile`.
   * @param data - Um objeto que implementa a interface `IUserFromBFF`, contendo os dados do usuário.
   * @return {Promise<void>} Uma promessa que resolve quando os dados do usuário forem definidos.
   **/
  async setDataUser(data: IUserFromBFF): Promise<void> {
    // recuperar o usuário e se tiver CEP, buscar as coordenadas
    const addressToFind = `${data.address?.addressLine1 ?? ''}, ${data.address?.addressLine2 ?? ''}, ${data.address?.city ?? ''} ${data.address?.state ?? ''}, ${data.address?.postalCode ?? ''} ${data.country?.countryCode ?? ''}`;
    const userAddressGoogleMaps = await firstValueFrom(
      this.#googleMapsApiService.geocodeBy(
        'address',
        addressToFind
      )
    );

    this.userCoordinates = userAddressGoogleMaps.results[0]?.geometry.location;
  }

  /**
   * Verifica se o usuário possui um endereço válido.
   *
   * Este método verifica se o endereço do usuário é válido, garantindo
   * que o endereço não seja nulo. A validação é baseada na presença
   * de campos obrigatórios no endereço.
   *
   * @returns {boolean} `true` se o endereço for válido e não nulo, caso contrário `false`.
   */
  hasAddress = computed<boolean>(() => {
    // Verifica se o endereço é válido com base em campos obrigatórios
    const address = this.userComputed()?.address;
    return address !== undefined &&
      address.addressLine1 !== undefined &&
      address.addressLine2 !== undefined &&
      address.postalCode !== undefined;
  });

  async loadUserProfile(): Promise<IUserProfileResponse> {
    const url = `${environment.getRoute(API.users)}/customers/user-profile`;
    const country = this.#getCountryService.getCountry();
    const headers = new HttpHeaders()
      .set('country', country)
      .set('region', 'SA');

    return await firstValueFrom(
      this.#http
        .get<IUserProfileResponse>(url, { headers })
        .pipe(
          tap((user) => {
            this.#userProfileSignal.set(user);
            sessionStorage.setItem(StorageKeys.USER_PROFILE, JSON.stringify(user));
          })
        )
    );
  }

  async saveUserProfile(
    body: IUserProfileResponse,
    awaitResponse = false
  ): Promise<void> {
    const url = `${environment.getRoute(API.users)}/customers/user-profile`;
    const userOrigin = this.#validateUserService.isMyFordAccountURL() ? 'MFA' : 'OSR';
    const headers = new HttpHeaders()
      .set('region', 'SA')
      .set('user-origin', userOrigin);

    const countryCode = body.country?.countryCode || this.#getCountryService.getCountry();

    const {
       phoneNumber = '',
       alternatePhoneNumber = '',
       mobilePhoneNumber = ''
     } = body.phoneNumbers ?? {};

    const phoneLength = getLengthPhone(countryCode);

    const user: IUserProfileResponse = {
      ...body,
      country: {
        ...body.country,
        countryCode,
      },
      names: {
        ...body.names,
        firstName: this.limitUserName(body.names?.firstName ?? ''),
        lastName: this.limitUserName(body.names?.lastName ?? ''),
      },
      phoneNumbers: {
        phoneNumber: phoneNumber ? phoneNumber.padStart(phoneLength, '0') : '',
        alternatePhoneNumber: alternatePhoneNumber ? alternatePhoneNumber.padStart(phoneLength, '0') : '',
        mobilePhoneNumber: mobilePhoneNumber ? mobilePhoneNumber.padStart(phoneLength, '0') : '',
      }
    }

    const request$ = this.#http.post<IUserProfileResponse>(url, user, { headers });

    // TODO: melhorar a forma de esperar a função
    if (awaitResponse) {
      await firstValueFrom(request$);
    } else {
      firstValueFrom(request$);
    }

    this.#userProfileSignal.set(user);
    sessionStorage.setItem(StorageKeys.USER_PROFILE, JSON.stringify(user));
  }

  updateCustomerPhone(areaCode: string, phoneNumber: string) {
    const api = environment.getRoute(API.users);
    const url = `${api}/customers/update/phone`;

    const body = {
      areaCode,
      phone: phoneNumber.replaceAll('-', '')
    };

    return this.#http.put(url, body);
  }

  private limitUserName(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length <= 2) {
      return name.trim();
    }
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

}
